import { z } from 'zod';
import { useMutation } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { useEffect, useState } from 'react';
import { useDebounceHandler } from '@lili/utils/hooks';
import { routesModel } from '@Router/router.consts';
import { RoutesMap } from '@Router/router.types';
import { OnboardingTypeEnum, useBoundStore } from '@Store/index';
import { updateCustomerDataFromServer } from '@Services/init/util';
import { showResumeApplicationModal } from '@Hooks/modalManager/showResumeApplicationModal.tsx';
import { buildNavigationStack, HttpService } from '@Services/index';
import { OnboardingFormFields } from '@Types/index';
import { useFormHook } from '@Hooks/index';

import { useNavigationManager } from '@Router/useNavigationManager';
import { recoveryStack } from '@Consts/navigation-stack-map';
import { OnboardingFlowIdentifier } from '@Types/onboarding.types';
import { identifyLogRocketUser } from '@Services/analytics/logrocket.service';
import { evaluateRainguard } from '@Services/rainguard/rainguard.service';
import { isDeclinedOnboardingStatus } from '@Utils/onboardingStatus';
import type { RegionCode } from 'google-libphonenumber';
const useLoginMutation = () => {
  return useMutation({
    mutationFn: async (data: { email: string; password: string }) => {
      const response = await HttpService.post('v5/onboarding/login', {
        email: data.email,
        password: data.password,
      });
      return response.data;
    },
    mutationKey: ['login'],
  });
};

export const useLoginLogic = () => {
  const { debounceAction } = useDebounceHandler(1501);
  const { mutateAsync: loginMutation, isPending } = useLoginMutation();
  const customerData = useBoundStore.getState().getCustomerData();
  const setOnboardingIdentifier = useBoundStore
    .getState()
    .onboardingSlice.getOnboardingData().setOnboardingIdentifier;
  const setNavigationStack = useBoundStore.getState().onboardingSlice.setNavigationStack;
  const setOnboardingAuthenticated = useBoundStore.getState().setOnboardingAuthenticated;
  const { email } = useSearch({ from: routesModel[RoutesMap.LOGIN].path });
  const [isForgotPasswordModalOpen, setIsForgotPasswordModalOpen] = useState(false);
  const { navigateToFirstPage, navigateToSpecificPage } = useNavigationManager();
  const { clearSlice } = useBoundStore.getState().resetPasswordSlice;

  useEffect(() => {
    clearSlice();
  }, []);

  const openForgotPasswordModal = () => {
    setIsForgotPasswordModalOpen(true);
  };

  const closeForgotPasswordModal = () => {
    setIsForgotPasswordModalOpen(false);
  };

  const initRecoveryFlow = () => {
    closeForgotPasswordModal();
    setNavigationStack(recoveryStack);
    navigateToFirstPage({ email: email || '' });
  };

  const handleLogin = debounceAction(async () => {
    const response = await loginMutation({
      email: formMethods.getValues(OnboardingFormFields.EMAIL),
      password: formMethods.getValues(OnboardingFormFields.PASSWORD),
    });

    if (
      response?.onboardingTypeEnum &&
      response?.onboardingTypeEnum === OnboardingTypeEnum.MULTI_BUSINESS
    ) {
      setOnboardingIdentifier(OnboardingFlowIdentifier.DIRECT_MULTI_BUSINESS);
    }

    // Declined customers have no valid token and cannot continue onboarding.
    // Route them to the KYC failed page instead of the MFA / recovery fallbacks.
    const isDeclined = isDeclinedOnboardingStatus(response?.onboardingStatus);

    if (!isDeclined && response?.mfaDestination && response?.mfaType && response?.stateToken) {
      const resetPasswordSlice = useBoundStore.getState().resetPasswordSlice;
      resetPasswordSlice.setEmail(formMethods.getValues(OnboardingFormFields.EMAIL));
      resetPasswordSlice.setRequestMfaType(response.mfaType);
      resetPasswordSlice.setMfaDestination(response.mfaDestination);
      resetPasswordSlice.setStateToken(response.stateToken);

      navigateToSpecificPage(RoutesMap.LOGIN_OTP_VERIFICATION);
      return;
    } else if (!isDeclined && response?.mfaDestination && response?.mfaType) {
      openForgotPasswordModal();
      return;
    }

    const customerDataToUpdate = { ...customerData };
    customerDataToUpdate.externalId = response.externalId;
    customerDataToUpdate.customerId = response.externalId;
    customerDataToUpdate.onboardingStatus = response.onboardingStatus;
    customerDataToUpdate.customerData.password = formMethods.getValues(
      OnboardingFormFields.PASSWORD
    );
    if (response?.onboardingTypeEnum) {
      customerDataToUpdate.customerData.onboardingTypeEnum = response.onboardingTypeEnum;
    }

    customerDataToUpdate.nameTooLong = response.nameTooLong;
    customerDataToUpdate.allowEditingPersonalDetails = response.allowEditingPersonalDetails;
    customerDataToUpdate.hasSoleProprietor = response.hasSoleProprietor;
    customerDataToUpdate.availablePromoCodes = response.availablePromoCodes;
    customerDataToUpdate.bank = response.bank;
    customerDataToUpdate.isNonUsSupported = response.isNonUsSupported;
    customerDataToUpdate.idCountry = response.idCountry;
    customerDataToUpdate.nationalIdDescription = response.nationalIdDescription;
    customerDataToUpdate.campaignName = response.campaignName;
    customerDataToUpdate.customerData = {
      ...customerDataToUpdate.customerData,
      ...response.customerData,
    };

    customerDataToUpdate.customerData.lastStepData = response?.lastStepData?.stepName
      ? {
          version: Number(response.lastStepData.version || 0),
          stepName: response.lastStepData.stepName,
        }
      : undefined;
    customerDataToUpdate.customerData.phoneCountryCode = response.phoneCountryCode as RegionCode;

    updateCustomerDataFromServer(customerDataToUpdate, customerData);
    setOnboardingAuthenticated(true);
    evaluateRainguard();
    identifyLogRocketUser(response.externalId, {
      isReturningUser: true,
      onboardingStatus: response.onboardingStatus,
      onboardingTypeEnum: response.onboardingTypeEnum,
    });
    buildNavigationStack({
      shouldHandleLoginMannually: true,
      onResume: () => showResumeApplicationModal(),
    });
  });

  const formMethods = useFormHook({
    formSchema: z.object({
      [OnboardingFormFields.EMAIL]: z.string().email(),
      [OnboardingFormFields.PASSWORD]: z.string().min(8),
    }),
    formDefaultValues: {
      [OnboardingFormFields.EMAIL]: email || '',
      [OnboardingFormFields.PASSWORD]: '',
    },
    schemaTitle: 'login',
  });

  return {
    formMethods,
    handleLogin,
    isPending,
    isForgotPasswordModalOpen,
    openForgotPasswordModal,
    closeForgotPasswordModal,
    initRecoveryFlow,
  };
};
